import React, { useState, useEffect } from "react";
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { Notifications } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { usePendingApprovalsForMe } from "services/hooks/auditApprovals";

const APPROVALS_INBOX_PATH = "/audit-approvals?tab=inbox&view=pending_for_me";

const ApprovalNotificationBell = ({
  enabled = true,
  iconSx = { fontSize: "25px", color: "white" },
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingCount, pendingItems, refetch } = usePendingApprovalsForMe({ enabled });

  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    refetch();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const goToInbox = () => {
    handleClose();
    navigate(APPROVALS_INBOX_PATH);
  };

  const tooltip =
    pendingCount > 0
      ? `${pendingCount} approval${pendingCount === 1 ? "" : "s"} need your action`
      : "No pending approvals";

  return (
    <>
      <Tooltip title={tooltip}>
        <Badge
          badgeContent={pendingCount}
          color="error"
          overlap="circular"
          max={99}
          sx={{ "& .MuiBadge-badge": { fontWeight: 700 } }}
        >
          <IconButton onClick={handleOpen} sx={{ color: "inherit" }}>
            <Notifications sx={iconSx} />
          </IconButton>
        </Badge>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { maxWidth: 400 } } }}
      >
        {pendingCount === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2">No pending approvals</Typography>
          </MenuItem>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">
                {pendingCount} item{pendingCount === 1 ? "" : "s"} need your action
              </Typography>
            </Box>
            <Divider />
            {pendingItems.map((item) => (
              <MenuItem
                key={item.id}
                onClick={goToInbox}
                sx={{ whiteSpace: "normal", alignItems: "flex-start" }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {item.summary || item.entity_label || `${item.entity_code} #${item.id}`}
                  </Typography>
                  {item.amount && (
                    <Typography variant="caption" color="text.secondary">
                      Amount: {item.amount}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem onClick={goToInbox} sx={{ fontWeight: 600 }}>
              Open Approvals inbox
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default ApprovalNotificationBell;
